import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import { GenericDetailPage, Tabs, Spinner } from '@repo/components';
import GroupInfoBox from 'src/components/GroupInfoBox';
import { AddOwnerForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { PERMISSIONS, Task } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroup } from 'src/api/personal/groups';
import { getGroupTasks, getGroupRoles, getGroupDocuments } from 'src/api/personal/groupDetail';

export const Route = createFileRoute('/personal_/groups/$groupId')({
	component: GroupDetailLayout,
});

function GroupDetailLayout() {
	const { groupId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const [isAddOwnerDialogOpen, setIsAddOwnerDialogOpen] = useState(false);

	const { data: group, isLoading: groupLoading } = useQuery({
		queryKey: ['personal-group', groupId],
		queryFn: () => getGroup(groupId),
		enabled: !authLoading && !!authenticatedUser && verifyPermission([PERMISSIONS.personalGroupsView]),
	});

	const { data: tasksData } = useQuery({
		queryKey: ['personal-group-tasks', groupId],
		queryFn: () => getGroupTasks(groupId),
		enabled: !!group,
	});

	const { data: groupRoles } = useQuery({
		queryKey: ['personal-group-roles', groupId],
		queryFn: () => getGroupRoles(groupId),
		enabled: !!group,
	});

	const { data: documentsData } = useQuery({
		queryKey: ['personal-group-documents', groupId],
		queryFn: () => getGroupDocuments(groupId),
		enabled: !!group && verifyPermission([PERMISSIONS.personalGroupsDocuments]),
	});

	const tasks = tasksData?.data || [];
	const hasTasksTodo = tasks.find((t: Task) => t.status === 'todo');
	const hasDocumentsForReview = (documentsData?.data || []).some(
		(doc: { status?: string }) => doc.status === 'inReview',
	);

	useBreadcrumbLevel(0, 'Personal Dashboard', '/personal');
	useBreadcrumbLevel(1, 'Companies', '/personal/groups');
	useBreadcrumbLevel(2, group?.name ?? 'Company');

	useEffect(() => {
		if (!authLoading && !verifyPermission([PERMISSIONS.personalGroupsView])) {
			navigate({
				to: '/personal/overview',
				search: { tab: '0', sort: 'timestamp:desc', search: undefined, zoneIds: undefined },
				replace: true,
			});
		}
	}, [authLoading, verifyPermission, navigate]);

	const { tabs, tabIndexPerTabId } = useMemo(() => {
		if (!group)
			return {
				tabs: [] as { id: string; label: string; error?: boolean }[],
				tabIndexPerTabId: {} as Record<string, number>,
			};
		const builtTabs = [
			...(group?.type?.Form ? [{ id: 'details', label: 'Details' }] : []),
			...(verifyPermission([PERMISSIONS.personalGroupsUsers]) ? [{ id: 'users', label: 'Users' }] : []),
			...(verifyPermission([PERMISSIONS.personalGroupsDocuments])
				? [{ id: 'documents', label: 'Documents', error: hasDocumentsForReview }]
				: []),
			{ id: 'tasks', label: 'Tasks', error: !!hasTasksTodo },
			...(verifyPermission([PERMISSIONS.personalGroupsEvents]) ? [{ id: 'events', label: 'Events' }] : []),
			...(verifyPermission([PERMISSIONS.personalGroupsLogs]) ? [{ id: 'logs', label: 'Logs' }] : []),
		];
		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => ({ ...acc, [cur.id]: index }),
			{} as Record<string, number>,
		);
		return { tabs: builtTabs, tabIndexPerTabId };
	}, [group, verifyPermission, hasDocumentsForReview, hasTasksTodo]);

	const pathSegments = location.pathname.split('/').filter(Boolean);
	const currentSegment = pathSegments[pathSegments.length - 1];
	const segmentBefore = pathSegments[pathSegments.length - 2];
	const isTasksDetail = segmentBefore === 'tasks' && currentSegment !== 'tasks';
	const effectiveTabId = isTasksDetail ? 'tasks' : currentSegment;
	const selectedTabIndex =
		(effectiveTabId != null ? tabIndexPerTabId[effectiveTabId] : undefined) ?? (tabs.length ? 0 : -1);

	const firstTabId = tabs[0]?.id;
	useEffect(() => {
		if (!group || !firstTabId) return;
		if (currentSegment === groupId) {
			navigate({ to: `/personal/groups/${groupId}/${firstTabId}`, replace: true });
		}
	}, [group, firstTabId, currentSegment, groupId, navigate]);

	useEffect(() => {
		if (authLoading || groupLoading || !verifyPermission([PERMISSIONS.personalGroupsView])) return;
		if (group) return;
		navigate({ to: '/personal/groups', replace: true });
	}, [authLoading, groupLoading, group, navigate, verifyPermission]);

	const handleTabChange = useCallback(
		(newValue: number) => {
			const tab = tabs[newValue];
			if (tab) {
				navigate({ to: `/personal/groups/${groupId}/${tab.id}` });
			}
		},
		[tabs, groupId, navigate],
	);

	const addOwners = useCallback(
		async (formData: Record<string, unknown> | undefined) => {
			if (!formData) return;
			try {
				await request.post(`/api/groups/${group!.id}/add-owners`, formData);
				EventEmitter.emit('toast', { type: 'success', message: 'Successfully added owner(s).' });
				setIsAddOwnerDialogOpen(false);
			} catch {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to add owner(s).' });
			}
		},
		[group],
	);

	if (authLoading || groupLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!verifyPermission([PERMISSIONS.personalGroupsView])) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!group) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	return (
		<>
			<div className="flex justify-center">
				<div className="max-w-[1280px] w-full">
					<GenericDetailPage>
						<GenericDetailPage.Content className="!px-0">
							<GenericDetailPage.Content.Notifications>
								{(hasDocumentsForReview || group.markedForReview?.length) && (
									<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
										This company is currently locked and requires to be reviewed.
										<ul>
											{hasDocumentsForReview && <li>This company has documents for review.</li>}
											{group.markedForReview?.includes('notUnique') && <li>VAT number is not unique</li>}
											{group.markedForReview?.includes('invalidVat') && <li>VAT number is not valid</li>}
											{group.markedForReview?.includes('vcaMissing') && (
												<li>No valid VCA documentation found on the company</li>
											)}
										</ul>
									</Alert>
								)}
								{hasTasksTodo && (
									<Alert severity="warning" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
										This company has open tasks.
									</Alert>
								)}
							</GenericDetailPage.Content.Notifications>
							<GroupInfoBox
								group={group}
								groupRoles={groupRoles || []}
								hasNoActions={true}
								openAddOwnerDialog={() => setIsAddOwnerDialogOpen(true)}
								permissions={{
									canAddOwner: verifyPermission([PERMISSIONS.personalGroupsEdit]),
								}}
								usesCompanyApiChecks={false}
							/>
						</GenericDetailPage.Content>
						<Tabs
							variant="underline"
							value={selectedTabIndex >= 0 ? selectedTabIndex : 0}
							onChange={(value) => handleTabChange(value)}
							className="px-4 border-b border-gray-300"
						>
							<Tabs.TabList>
								{tabs.map((tab, index) => (
									<Tabs.Tab
										key={tab.id}
										value={index}
										label={
											<div className="flex items-center gap-2">
												{tab.label}
												{tab.error && <span className="inline-flex h-2 w-2 rounded-full bg-red-600" />}
											</div>
										}
									/>
								))}
							</Tabs.TabList>
						</Tabs>
						<Outlet />
					</GenericDetailPage>
				</div>
			</div>

			<AddOwnerForm
				isOpen={isAddOwnerDialogOpen}
				onClose={() => setIsAddOwnerDialogOpen(false)}
				onSubmit={addOwners}
				source="company"
			/>
		</>
	);
}
